import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useBillingStatus } from '../hooks/useBilling';
import { useSubscriptions } from '../hooks/useSubscriptions';
import { canAccessBilling } from '../utils/permissions';
import AppLayout from '../components/layout/AppLayout';
import Card from '../components/common/Card';
import Spinner from '../components/common/Spinner';
import StripeCard from '../components/billing/StripeCard';
import LegacyBillingCard from '../components/billing/LegacyBillingCard';
import InvoiceBillingCard from '../components/billing/InvoiceBillingCard';
import OtherBillingCard from '../components/billing/OtherBillingCard';
import MigrateModal from '../components/billing/MigrateModal';

// Shared by the `!hasAccess` branch and the 403-from-the-server branch below
// (client-side gate vs. server-side gate hitting the same "no billing role"
// condition) so the two can't drift out of copy sync.
function PermissionDeniedCard() {
  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
      </div>
      <Card className="p-6">
        <p className="text-gray-600">
          You don't have permission to access billing information.
          Please contact an administrator.
        </p>
      </Card>
    </AppLayout>
  );
}

export default function BillingPage() {
  const { user } = useAuth();
  const { data: billing, isLoading, error } = useBillingStatus();
  const { data: subscriptions } = useSubscriptions({
    refetchInterval: (query) => {
      const subs = query.state.data;
      return subs?.some(s =>
        s.dedicated_ip_status === 'provisioning' || s.dedicated_ip_status === 'releasing'
      ) ? 10_000 : false;
    },
  });
  const [showMigrateModal, setShowMigrateModal] = useState(false);

  // Check if user has access (admin or billing role)
  const hasAccess = canAccessBilling(user);

  if (!hasAccess) {
    return <PermissionDeniedCard />;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        </div>
        <Card className="p-6">
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Card>
      </AppLayout>
    );
  }

  if (error) {
    // A 403 here means the caller genuinely lacks the billing role (e.g. a
    // residual race right after impersonation exit/switch, before the
    // still-mounted query's cache is cleared) — it's an access condition,
    // not a system failure, so show the same copy as the !hasAccess card
    // above instead of a scary red error. Log it though: hasAccess already
    // passed client-side, so a 403 here is either that race or a genuine
    // backend/frontend role drift — either way, worth knowing about instead
    // of silently rendering a permission wall that looks routine.
    if (isAxiosError(error) && error.response?.status === 403) {
      console.error(
        'Billing 403 for a user who passed the client-side role gate — impersonation race or backend auth fault',
        error
      );
      return <PermissionDeniedCard />;
    }

    return (
      <AppLayout>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        </div>
        <Card className="p-6">
          <p className="text-red-600">
            Failed to load billing information. Please try again later.
          </p>
        </Card>
      </AppLayout>
    );
  }

  const renderBillingCard = () => {
    if (!billing) {
      return <OtherBillingCard />;
    }

    switch (billing.provider) {
      case 'stripe':
        return <StripeCard billing={billing} subscriptions={subscriptions} />;
      case 'stripe_invoice':
        return <InvoiceBillingCard billing={billing} subscriptions={subscriptions} />;
      case 'foxy':
      case 'billsby':
        return (
          <LegacyBillingCard
            billing={billing}
            onMigrate={() => setShowMigrateModal(true)}
            subscriptions={subscriptions}
          />
        );
      default:
        return <OtherBillingCard />;
    }
  };

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="text-gray-600 mt-1">
          Manage your subscription and payment details
        </p>
      </div>

      <div className="max-w-2xl">{renderBillingCard()}</div>

      <MigrateModal
        isOpen={showMigrateModal}
        onClose={() => setShowMigrateModal(false)}
        billing={billing}
      />
    </AppLayout>
  );
}
